import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { COLORS, Panel, Table, Badge, Spinner, Banner, dateTime } from '../components/ui';

/**
 * Bulk run history, read from the audit table — not from memory, so it survives
 * restarts. This is the answer to "who issued 400 work orders on Tuesday".
 */
export default function BulkHistoryPage() {
    const [runs, setRuns] = useState(null);
    const [detail, setDetail] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => { api.bulkHistory().then(setRuns).catch(e => setError(e.message)); }, []);

    const openRun = async (run) => {
        setDetail({ runId: run.run_id, rows: null });
        try { setDetail({ runId: run.run_id, rows: await api.bulkHistoryRun(run.run_id) }); }
        catch (e) { setError(e.message); }
    };

    if (error) return <Banner tone="danger">{error}</Banner>;
    if (!runs) return <Spinner />;

    return (
        <div>
            <h1 style={{ fontSize: 22, margin: '0 0 4px', color: COLORS.ink }}>Bulk run history</h1>
            <p style={{ color: COLORS.ink2, fontSize: 13, marginTop: 0 }}>
                Every bulk run ever executed, one row per work order underneath. From
                mcweb.BulkOperation on unt-pbisql. <Link to="/bulk" style={{ color: COLORS.link }}>New run</Link>
            </p>

            <Panel title={`${runs.length} runs`} subtitle="Click a run to see every work order it touched">
                <Table
                    rowKey={r => r.run_id}
                    onRowClick={openRun}
                    columns={[
                        { key: 'performed_at', label: 'When', render: r => dateTime(r.performed_at) },
                        { key: 'performed_by', label: 'Who' },
                        { key: 'operation', label: 'Operation' },
                        { key: 'field', label: 'Field' },
                        { key: 'environment', label: 'Environment' },
                        { key: 'source', label: 'Verified via', render: r => r.source || '—' },
                        { key: 'dry_run', label: 'Mode', render: r => r.dry_run ? <Badge value="dry-run" /> : 'applied' },
                        { key: 'rows_total', label: 'Rows', align: 'right' },
                        { key: 'updated', label: 'Updated', align: 'right' },
                        { key: 'verified', label: 'Verified', align: 'right' },
                        { key: 'skipped', label: 'Skipped', align: 'right' },
                        { key: 'blocked', label: 'Blocked', align: 'right' },
                        {
                            key: 'failed', label: 'Failed', align: 'right',
                            render: r => <span style={{ color: r.failed ? COLORS.critical : COLORS.ink, fontWeight: r.failed ? 600 : 400 }}>{r.failed}</span>
                        },
                        {
                            key: 'unverified', label: 'Unverified', align: 'right',
                            render: r => <span style={{ color: r.unverified ? COLORS.critical : COLORS.ink, fontWeight: r.unverified ? 600 : 400 }}>{r.unverified}</span>
                        },
                    ]}
                    rows={runs}
                    maxHeight={420}
                />
            </Panel>

            {detail && (
                <Panel title={`Run ${detail.runId}`} subtitle="One row per work order, as written at the time">
                    {!detail.rows ? <Spinner /> : (
                        <Table
                            rowKey={(r, i) => `${r.wopk}-${i}`}
                            columns={[
                                { key: 'woid', label: 'WO', render: r => <Link to={`/wo/${r.wopk}`} style={{ color: COLORS.link }}>{r.woid}</Link> },
                                { key: 'field', label: 'Field' },
                                { key: 'old_value', label: 'Was' },
                                { key: 'new_value', label: 'Set to' },
                                { key: 'outcome', label: 'Outcome', render: r => <Badge value={r.outcome} /> },
                                { key: 'verified', label: 'Verified', render: r => r.verified ? 'yes' : '' },
                                { key: 'verified_value', label: 'DB held' },
                                { key: 'error_message', label: 'Detail', wrap: true, render: r => <span style={{ color: COLORS.muted }}>{r.error_message || ''}</span> },
                            ]}
                            rows={detail.rows}
                        />
                    )}
                </Panel>
            )}
        </div>
    );
}
